import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import './pages.css';

interface CategorizationFile {
  filename: string;
  size: number;
  created: string;
  modified: string;
}

const Categorization: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [fileInputRef, setFileInputRef] = useState<HTMLInputElement | null>(null);
  const [sheetName, setSheetName] = useState<string>('');
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [availableFiles, setAvailableFiles] = useState<CategorizationFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [categorizationMethod, setCategorizationMethod] = useState<string>('id-based');

  const loadAvailableFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const response = await apiClient.get('/categorization/files/');
      setAvailableFiles(response.data.files || []);
    } catch (err: any) {
      console.error('Error loading files:', err);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    loadAvailableFiles();
  }, [loadAvailableFiles]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setSheetName('');
      setAvailableSheets([]);
      setError(null);
      setSuccess(false);
      
      // Get sheet names from backend
      setLoadingSheets(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      try {
        const response = await apiClient.post('/categorization/sheets/', formData);
        setAvailableSheets(response.data.sheets || []);
      } catch (err: any) {
        const statusCode = err.response?.status;
        const errorData = err.response?.data;
        const is413Error = statusCode === 413 ||
          err.message?.includes('413') ||
          (typeof errorData === 'string' && errorData.includes('413'));

        if (is413Error) {
          setError('The file you selected is too large to upload. The maximum file size is 300 MB.  Please try with a smaller file or reduce the file size before uploading.');
        } else {
          setError(err.response?.data?.error || 'Error reading file sheets');
        }
      } finally {
        setLoadingSheets(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!sheetName) {
      setError('Please select a sheet');
      return;
    }

    // Get file from input element to ensure it's fresh
    let fileToSubmit: File | null = null;
    if (fileInputRef && fileInputRef.files && fileInputRef.files[0]) {
      fileToSubmit = fileInputRef.files[0];
    } else if (file) {
      fileToSubmit = file;
    }

    if (!fileToSubmit) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    const formData = new FormData();
    formData.append('file', fileToSubmit);
    formData.append('sheet_name', sheetName);
    formData.append('use_semantic_categorization', categorizationMethod === 'semantic' ? 'true' : 'false');

    try {
      await apiClient.post('/categorization/categorize/', formData);
      
      // Reload available files after successful categorization
      await loadAvailableFiles();
      
      setSuccess(true);
    } catch (err: any) {
      console.error('Categorization error:', err);
      const errorData = err.response?.data;
      const statusCode = err.response?.status;

      // Build a detailed error message
      let errorMessage = 'An error occurred during categorization';

      // Handle 413 error (file too large) - can come from nginx or backend
      const is413Error = statusCode === 413 ||
        err.message?.includes('413') ||
        (typeof errorData === 'string' && errorData.includes('413'));

      if (is413Error) {
        errorMessage = 'The file you selected is too large to upload. The maximum file size is 300 MB.  Please try with a smaller file or reduce the file size before uploading.';
      } else if (errorData) {
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          // If errors is an array, format it nicely
          errorMessage = 'Validation failed:\n' + errorData.errors.map((e: string) => `• ${e}`).join('\n');
        } else if (typeof errorData.errors === 'string') {
          errorMessage = errorData.errors;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedFile) {
      setError('Please select a file to download');
      return;
    }

    try {
      const response = await apiClient.get(`/categorization/files/${selectedFile}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', selectedFile);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred while downloading the file');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="page-container">
      <h1 className="page-title">URS Categorization Tool</h1>
      <div className="page-description">
        <p>
          This application enables you to upload an <strong>URS file</strong> (User Requirement Specifications) in Excel format
          and process it to classify requirements into <strong>sections</strong> and <strong>subsections</strong>.
        </p>
        <h3>How to Use:</h3>
        <ol>
          <li><strong>Upload your Excel file:</strong> Ensure it contains columns such as <code>id</code> and <code>requirements</code>.</li>
          <li><strong>Select the sheet:</strong> Choose the sheet from your uploaded file that contains the requirement data.</li>
          <li><strong>Click Execute Categorization</strong> to run the categorization.</li>
          <li><strong>Download the output</strong> for further analysis.</li>
        </ol>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="upload-section">
          <label className="upload-label" htmlFor="file">
            Choose an Excel file
          </label>
          <input
            id="file"
            type="file"
            accept=".xlsx"
            ref={(input) => setFileInputRef(input)}
            onChange={handleFileChange}
            className="file-input"
            disabled={loading || loadingSheets}
          />
        </div>

        {loadingSheets && (
          <p className="text-secondary-margin">Reading file sheets...</p>
        )}

        {availableSheets.length > 0 && (
          <div className="upload-section">
            <label className="upload-label" htmlFor="sheet">
              Select a sheet
            </label>
            <select
              id="sheet"
              value={sheetName}
              onChange={(e) => setSheetName(e.target.value)}
              className="file-input"
              disabled={loading}
            >
              <option value="">Select a sheet...</option>
              {availableSheets.map((sheet) => (
                <option key={sheet} value={sheet}>
                  {sheet}
                </option>
              ))}
            </select>
          </div>
        )}

        {sheetName && (
          <div className="upload-section">
            <label className="upload-label">Categorization Method</label>
            <div className="method-selection">
              <label>
                <input
                  type="radio"
                  value="id-based"
                  checked={categorizationMethod === 'id-based'}
                  onChange={(e) => setCategorizationMethod(e.target.value)}
                  disabled={loading}
                />
                Infer from IDs
              </label>
              <label>
                <input
                  type="radio"
                  value="semantic"
                  checked={categorizationMethod === 'semantic'}
                  onChange={(e) => setCategorizationMethod(e.target.value)}
                  disabled={loading}
                />
                Infer from semantic meaning
              </label>
            </div>
            <div className="method-help">
              <p>Choose 'Infer from IDs' if your IDs contain hierarchical information (like 1.1, 1.2, 2.1).</p>
              <p>Choose 'Infer from semantic meaning' if IDs are sequential (like 1, 2, 3, ...) and hierarchy should be based on content meaning.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message" style={{ whiteSpace: 'pre-line' }}>
            {error}
          </div>
        )}
        {success && <div className="success-message">Categorization completed successfully! Select a file from the list below to download.</div>}

        <button type="submit" className="button" disabled={!file || !sheetName || loading}>
          {loading ? 'Processing...' : 'Execute Categorization'}
        </button>
      </form>

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Processing your file...</p>
        </div>
      )}

      <div className="results-section">
        <h2 className="results-title">Available Categorization Results</h2>
        
        {loadingFiles ? (
          <p>Loading files...</p>
        ) : availableFiles.length === 0 ? (
          <p className="text-secondary">No categorization results available yet.</p>
        ) : (
          <>
            <div className="upload-section">
              <label className="upload-label" htmlFor="fileSelect">
                Select a file to download
              </label>
              <select
                id="fileSelect"
                value={selectedFile}
                onChange={(e) => setSelectedFile(e.target.value)}
                className="file-input select-margin-bottom"
              >
                <option value="">Select a file...</option>
                {availableFiles.map((file) => (
                  <option key={file.filename} value={file.filename}>
                    {file.filename} ({formatFileSize(file.size)}) - {formatDate(file.modified)}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="button"
              onClick={handleDownload}
              disabled={!selectedFile}
            >
              Download Selected File
            </button>

            <button
              type="button"
              className="button button-refresh"
              onClick={loadAvailableFiles}
            >
              Refresh List
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Categorization;
